/**
 * The emailed link lands here.
 *
 * ACTIVATION NEEDS THE PIN: the link alone does not activate an account. The
 * person types the 4-digit PIN they chose when they signed up; a wrong PIN
 * does not activate and does not burn the link, so a link read by someone else
 * leaves the account pending until it expires.
 *
 * Every screen here is drawn by the SAME PortalView the member's portal and the
 * designer's preview use, so the organization's own wording and colours apply.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import PortalView from '../../components/loyalty/PortalView';
import { Screen } from '../../components/loyalty/ui';
import { handOverSession, rememberMe } from './session';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api`;

/** NEVER A BLANK PAGE. With no card id, or when the settings cannot be read,
 *  the screen still draws itself on the default look: the slots the PIN
 *  screens need, visible, with the engine's own EN/FR/ES wording. */
const DEFAULT_CFG = {
    slots: [
        { id: 'confirm_pin_title', kind: 'text', default_key: 'loyalty.confirmPinTitle', visible: true },
        { id: 'confirm_pin_submit', kind: 'button', default_key: 'loyalty.confirmPinSubmit', visible: true },
        { id: 'pin_reset_title', kind: 'text', default_key: 'loyalty.choosePin', visible: true },
        { id: 'pin_reset_submit', kind: 'button', default_key: 'loyalty.pinResetSubmit', visible: true },
        { id: 'pin_reset_done', kind: 'text', default_key: 'loyalty.pinChanged', visible: true },
        { id: 'confirmed_title', kind: 'text', default_key: 'loyalty.confirmedTitle', visible: true },
        { id: 'confirmed_body', kind: 'text', default_key: 'loyalty.confirmedBody', visible: true },
        { id: 'confirmed_continue', kind: 'button', default_key: 'loyalty.confirmedContinue', visible: true },
    ],
};

export default function LoyaltyConfirm() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const token = params.get('token');
    const pinToken = params.get('pin_token');
    const cardParam = params.get('card');

    const [screen, setScreen] = useState(pinToken ? 'pin_reset' : 'confirm_pin');
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ pin: '' });
    const [points, setPoints] = useState(null);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const loadCfg = useCallback(async (cardId) => {
        if (!cardId) return;
        try {
            const r = await fetch(`${API}/loyalty/public/${cardId}?lang=${lang}`);
            if (r.ok) setCfg(await r.json());
        } catch { /* the screen still works on the default look */ }
    }, [lang]);

    useEffect(() => { loadCfg(cardParam); }, [cardParam, loadCfg]);

    const post = async (path, body) => {
        setBusy(true); setError('');
        try {
            const r = await fetch(`${API}${path}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                const reason = typeof data.detail === 'object' ? data.detail.reason : data.detail;
                setError(t(`loyalty.error.${reason}`,
                    t('loyalty.error.generic', 'This link is no longer valid')));
                return null;
            }
            return data;
        } finally { setBusy(false); }
    };

    const openPortal = () => {
        window.location.href = `/loyalty/${cfg?.card_id || cardParam || ''}`;
    };

    const on = {
        setForm,
        confirmPin: async () => {
            const out = await post('/loyalty/public/confirm', { token, pin: form.pin });
            if (!out) return;
            setPoints(out.points);
            // The PIN was just proved: keep the session and this device's email,
            // then walk INSIDE the app to the portal — no reload, no second PIN.
            handOverSession(out.session || '');
            rememberMe({ email: out.email || '', first_name: out.first_name || '' });
            const card = out.card_id || cfg?.card_id || cardParam || '';
            if (out.session && card) {
                navigate(`/loyalty/${card}`, { replace: true });
                return;
            }
            loadCfg(card);
            setScreen('confirmed');
        },
        setNewPin: async () => {
            const out = await post('/loyalty/public/pin-reset/confirm',
                { token: pinToken, pin: form.pin });
            if (out) setDone(true);
        },
        continueToPortal: openPortal,
    };

    if (!token && !pinToken) {
        return (
            <Screen testid="loyalty-confirm-failed">
                <p className="text-center text-base opacity-80">
                    {t('loyalty.error.generic', 'This link is no longer valid')}
                </p>
            </Screen>
        );
    }

    if (busy && !cfg) {
        return <Screen testid="loyalty-confirm"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></Screen>;
    }

    return (
        <PortalView screen={screen} cfg={cfg || DEFAULT_CFG} form={form} error={error} busy={busy}
                    me={points != null ? { points } : null} saved={{ done }} on={on} />
    );
}
